import { useState, useRef } from 'react';
import { Smartphone, FileText, Truck, Gift, ShoppingCart, Award, Shield, CreditCard, Umbrella, X } from 'react-feather';
import useIntersectionObserver from '../hooks/useIntersectionObserver';
import './FeaturesSection.css';

const features = [
  { 
    icon: Smartphone, 
    title: 'Mobile Recharge', 
    desc: 'Fast prepaid & postpaid recharges in seconds.',
    color: '#F6A10D'
  },
  { 
    icon: FileText, 
    title: 'Utility Bill Payments', 
    desc: 'Pay electricity, water, gas & broadband bills easily.',
    color: '#FFCF0D'
  },
  { 
    icon: Truck, 
    title: 'Bus Ticket Booking', 
    desc: 'Book intercity buses instantly.',
    color: '#9F37FA'
  },
  { 
    icon: Gift, 
    title: 'Gift Cards', 
    desc: 'Buy popular brand gift cards anytime.',
    color: '#7917E0'
  },
  { 
    icon: ShoppingCart, 
    title: 'Online Shopping', 
    desc: 'Shop products directly inside the app.',
    color: '#667eea'
  },
  { 
    icon: Award, 
    title: 'Rewards & Cashback', 
    desc: 'Earn rewards on recharges and shopping.',
    color: '#f093fb'
  },
  { 
    icon: Shield, 
    title: 'Secure Payments', 
    desc: 'Safe and reliable transactions.',
    color: '#FF4757'
  },
];

function FeaturesSection() {
  const [showPopup, setShowPopup] = useState(false);
  const { sectionRef, isVisible } = useIntersectionObserver();

  return (
    <section id="features" className="features-section" ref={sectionRef}>
      <h2 className={`features-title ${isVisible ? 'animate' : ''}`}>Our Features</h2>
      
      <div className="features-container">
        {/* Phone SVG Mockup */}
        <div className={`phone-mockup ${isVisible ? 'animate' : ''}`}>
          <svg className="phone-svg" viewBox="0 0 320 640" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Phone body with gradient */}
            <defs>
              <linearGradient id="phoneGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style={{ stopColor: '#667eea', stopOpacity: 0.8 }} />
                <stop offset="50%" style={{ stopColor: '#764ba2', stopOpacity: 0.6 }} />
                <stop offset="100%" style={{ stopColor: '#f093fb', stopOpacity: 0.8 }} />
              </linearGradient>
              <linearGradient id="screenGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" style={{ stopColor: '#1a1a2e', stopOpacity: 1 }} />
                <stop offset="100%" style={{ stopColor: '#16213e', stopOpacity: 1 }} />
              </linearGradient>
              <filter id="glow">
                <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
                <feFlood floodColor="#ffffff" floodOpacity="0.8" result="whiteFlood"/>
                <feComposite in="whiteFlood" in2="coloredBlur" operator="in" result="whiteGlow"/>
                <feGaussianBlur in="whiteGlow" stdDeviation="6" result="finalGlow"/>
                <feMerge>
                  <feMergeNode in="finalGlow"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
            
            {/* Phone outer frame */}
            <rect x="10" y="10" width="300" height="620" rx="40" fill="url(#phoneGradient)" opacity="0.3"/>
            <rect x="12" y="12" width="296" height="616" rx="38" fill="#0a0a0a" opacity="0.95"/>
            
            {/* Phone screen */}
            <rect x="20" y="80" width="280" height="540" rx="25" fill="url(#screenGradient)"/>
            
            {/* Notch */}
            <rect x="110" y="25" width="100" height="28" rx="14" fill="#0a0a0a"/>
            <circle cx="135" cy="39" r="4" fill="#1a1a2e" opacity="0.5"/>
            <rect x="150" y="36" width="40" height="6" rx="3" fill="#1a1a2e" opacity="0.5"/>
            
            {/* Screen elements with glow */}
            <g filter="url(#glow)">
              {/* Status bar icons */}
              <rect x="40" y="100" width="30" height="4" rx="2" fill="#667eea" opacity="0.6"/>
              <rect x="250" y="100" width="30" height="4" rx="2" fill="#f093fb" opacity="0.6"/>
              
              {/* App icons grid */}
              <rect x="40" y="130" width="50" height="50" rx="12" fill="#667eea" opacity="0.3"/>
              <rect x="105" y="130" width="50" height="50" rx="12" fill="#764ba2" opacity="0.3"/>
              <rect x="170" y="130" width="50" height="50" rx="12" fill="#f093fb" opacity="0.3"/>
              <rect x="235" y="130" width="50" height="50" rx="12" fill="#FFCF0D" opacity="0.3"/>
              
              <rect x="40" y="195" width="50" height="50" rx="12" fill="#F6A10D" opacity="0.3"/>
              <rect x="105" y="195" width="50" height="50" rx="12" fill="#9F37FA" opacity="0.3"/>
              <rect x="170" y="195" width="50" height="50" rx="12" fill="#667eea" opacity="0.3"/>
              <rect x="235" y="195" width="50" height="50" rx="12" fill="#f093fb" opacity="0.3"/>
              
              {/* Featured card */}
              <rect x="40" y="270" width="240" height="120" rx="20" fill="#667eea" opacity="0.2"/>
              <rect x="50" y="285" width="80" height="8" rx="4" fill="#ffffff" opacity="0.7"/>
              <rect x="50" y="305" width="140" height="6" rx="3" fill="#ffffff" opacity="0.4"/>
              <circle cx="250" cy="320" r="25" fill="#f093fb" opacity="0.4"/>
              
              {/* Transaction list */}
              <rect x="40" y="410" width="240" height="30" rx="8" fill="#ffffff" opacity="0.05"/>
              <rect x="40" y="450" width="240" height="30" rx="8" fill="#ffffff" opacity="0.05"/>
              <rect x="40" y="490" width="240" height="30" rx="8" fill="#ffffff" opacity="0.05"/>
            </g>
            
            {/* Side buttons */}
            <rect x="0" y="120" width="8" height="50" rx="4" fill="url(#phoneGradient)" opacity="0.7"/>
            <rect x="0" y="200" width="8" height="40" rx="4" fill="url(#phoneGradient)" opacity="0.7"/>
            <rect x="312" y="150" width="8" height="80" rx="4" fill="url(#phoneGradient)" opacity="0.7"/>
            
            {/* Bottom indicator */}
            <rect x="130" y="600" width="60" height="5" rx="2.5" fill="#ffffff" opacity="0.3"/>
          </svg>
        </div>

        {/* Features Grid */}
        <div className="features-grid-container">
          {features.map((feature, idx) => {
            const IconComponent = feature.icon;
            return (
              <div 
                key={idx} 
                className={`feature-card ${isVisible ? 'animate' : ''}`}
                style={{ '--feature-color': feature.color }}
              >
                <div className="feature-icon-wrapper">
                  <IconComponent />
                </div>
                <div>
                  <h3 className="feature-title">{feature.title}</h3>
                  <p className="feature-description">{feature.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <button className="coming-soon-btn" onClick={() => setShowPopup(true)}>
        Coming Soon
      </button>

      {/* Coming Soon Popup */}
      {showPopup && (
        <div className="popup-overlay" onClick={() => setShowPopup(false)}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <button className="popup-close" onClick={() => setShowPopup(false)}>
              <X size={24} />
            </button>
            <h2 className="popup-title">Coming Soon</h2>
            <div className="popup-features">
              <div className="popup-feature">
                <div className="popup-feature-icon">
                  <CreditCard size={28} />
                </div>
                <div>
                  <h3>Credit Card Services</h3>
                  <p>Manage and apply for credit cards.</p>
                </div>
              </div>
              <div className="popup-feature">
                <div className="popup-feature-icon">
                  <Umbrella size={28} />
                </div>
                <div>
                  <h3>Insurance</h3>
                  <p>Buy and manage insurance policies.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default FeaturesSection;
